import React, { useEffect, useReducer, useState, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Form,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Table,
  Row,
  Col,
} from 'reactstrap';

import { useFormik } from 'formik';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import Wizard from '../../../Components/Common/Wizard';
import CsvImporter from '../../../Components/Common/CsvImporter';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import CurrencyInput from '../../../Components/Common/CurrencyInput';
import {
  batchImportIncentive,
  getAllIncentiveGroups,
  validateIncentiveFile,
} from '../../../slices/thunks';
import { currencyFieldToNumber } from '../../../helpers/functions';
import { formatDate, formatCurrency } from '../../../helpers/format';
import { removeBomFromBinaryFile } from '../../../helpers/encode_file';
import CsvFieldsList from '../../../Components/Common/CsvFieldsList';

const initialValues = {
  cashbackAmount: '',
  cashbackExpiresAt: '',
  incentiveGroupId: '',
  fields: undefined,
  rows: 0,
};

const IncentiveImporter = ({ isOpen, toggle, onSuccess, t, tReady }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [groups, setGroups] = useState([]);
  const dispatch = useDispatch();

  const getAllGroups = async () => {
    const action = await dispatch(
      getAllIncentiveGroups({
        take: 99999,
        filters: {
          type: { in: ['user'] },
        },
      })
    );

    if (!action.payload || action.error) {
      return;
    }

    setGroups(
      action.payload.map((item) => ({
        label: item.name,
        value: item.id,
      }))
    );
  };

  const [payload, setPayload] = useReducer(
    (acc, item) => ({ ...acc, ...item }),
    initialValues
  );

  const [validationData, setValidationData] = useState(null);

  useEffect(() => {
    getAllGroups();
  }, [isOpen]);

  const handleValidationResult = (validationResult) => {
    if (validationResult?.data?.validationData) {
      setValidationData(validationResult.data.validationData);

      setPayload({
        validationData: validationResult.data.validationData,
        detectedColumns:
          validationResult.data.validationData.detectedColumns || [],
      });
    }
  };

  const handleFileValidation = async (file, data) => {
    if (!(file instanceof File || file instanceof Blob)) {
      return { error: true, message: 'Arquivo inválido!' };
    }

    const configurationParse = data?.columnFields?.reduce(
      (current, value, index) => {
        const column = data?.preview?.columns?.find(
          (val) => val.index === index
        );
        return {
          ...current,
          [value || '']: { column: column?.header },
        };
      },
      {}
    );

    try {
      const result = await dispatch(
        validateIncentiveFile({
          file,
          configuration_parse: JSON.stringify(configurationParse),
        })
      ).unwrap();
      handleValidationResult(result);
      return result;
    } catch (error) {
      return { error: true, message: error.message };
    }
  };

  const hasCashbackAmount = useMemo(() => {
    if (!validationData?.detectedColumns) return false;

    const hasCB = validationData.detectedColumns.includes('cashback_amount') ||
      validationData.detectedColumns.includes('valor_do_cashback') ||
      validationData.detectedColumns.includes('Valor do cashback')
    return (
      hasCB
    );
  }, [validationData]);

  const hasCashbackExpiresAt = useMemo(() => {
    if (!validationData?.detectedColumns) return false;

    return (
      validationData.detectedColumns.includes('cashback_expires_at') ||
      validationData.detectedColumns.includes(
        'data_de_expiracao_do_cashback'
      ) ||
      validationData.detectedColumns.includes('Data de expiração do cashback')
    );
  }, [validationData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async () => {
      setIsLoading(true);

      try {
        const cleanFile = await removeBomFromBinaryFile(payload.file);

        const {
          columnFields,
          preview,
          cashbackAmount,
          cashbackExpiresAt,
          incentiveGroupId,
        } = payload;

        const configurationParse = columnFields.reduce(
          (currentvalue, value, index) => {
            const column = preview.columns.find((val) => val.index === index);
            return {
              ...currentvalue,
              [value || '']: { column: column?.header },
            };
          },
          {}
        );

        const data = {
          file: cleanFile,
          cashback_amount: cashbackAmount
            ? currencyFieldToNumber(cashbackAmount)
            : null,
          cashback_expires_at: cashbackExpiresAt
            ? moment(cashbackExpiresAt).toISOString()
            : null,
          incentive_group_id: incentiveGroupId,
          configuration_parse: JSON.stringify(configurationParse),
        };

        const action = await dispatch(
          batchImportIncentive({
            ...data,
            successMessage: t('import-success'),
            errorMessage: t('import-error'),
          })
        );

        if (!action.payload.error) {
          resetState();
          onSuccess();
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  const resetState = () => {
    setTimeout(() => {
      setActiveTab(1);
      setPayload(initialValues);
      formik.resetForm();
    }, 1000);
    toggle();
  };

  return (
    <Modal
      centered
      isOpen={isOpen}
      size={activeTab >= 2 ? 'lg' : 'xl'}
      style={{ transition: 'all 0.2s ease' }}
      toggle={resetState}
      id="showModal"
    >
      <ModalHeader className="bg-info-subtle p-3" toggle={toggle}>
        {tReady && t('incentive-import-modal-heading')}
      </ModalHeader>

      <Form
        className="tablelist-form"
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
          return false;
        }}
      >
        <ModalBody>
          <Wizard
            showSteps
            activeTab={activeTab}
            tabs={[
              {
                label: tReady && t('incentive-import-modal-step1-heading'),
                component: (
                  <>
                    <div className="row">
                      <div className="col-md-6">
                        <CsvFieldsList
                          fields={[
                            {
                              labelKey: 'import-document-prop',
                              descriptionKey: 'document-field-description',
                              tooltipKey: 'document-tooltip',
                              required: true,
                            },
                            {
                              labelKey: 'import-name-prop',
                              tooltipKey: 'name-tooltip',
                              required: false,
                            },
                            {
                              labelKey: 'import-email-prop',
                              tooltipKey: 'email-tooltip',
                              required: false,
                            },
                            {
                              labelKey: 'import-birthdate-prop',
                              tooltipKey: 'birthdate-tooltip',
                              required: false,
                            },
                            {
                              labelKey: 'import-cellphone-prop',
                              tooltipKey: 'cellphone-tooltip',
                              required: false,
                            },
                            {
                              labelKey: 'import-cashbackamount-prop',
                              tooltipKey: 'cashback-amount-tooltip',
                              required: false,
                            },
                            {
                              labelKey: 'import-cashbackexpiresat-prop',
                              tooltipKey: 'cashback-expires-tooltip',
                              required: false,
                            },
                          ]}
                        />
                      </div>
                      <div className="col-md-6">
                        <h6>
                          {tReady && t('incentive-import-modal-step1-heading')}
                        </h6>
                        <p className="d-w-100 mb-3">
                          {tReady &&
                            t('incentive-import-modal-step1-description')}
                        </p>

                        <button
                          className="btn btn-outline-primary mb-4"
                          onClick={() =>
                            window.open(
                              '/templates/incentive-import-template.csv',
                              '_blank'
                            )
                          }
                        >
                          <i className="ri-download-line me-1"></i>
                          {t('download-template')}
                        </button>
                      </div>
                    </div>

                    {/* {validationData?.errorCount > 0 && (
                      <div className="alert alert-danger d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <strong>Erros de validação encontrados</strong>
                          <br />
                          {validationData.errorCount} erros encontrados em{' '}
                          {validationData.totalRows} linhas.
                          <br />
                          {validationData.validRows} linhas válidas,{' '}
                          {validationData.invalidRows} com problemas.
                          <br />
                          Por favor, baixe o arquivo de erros para verificar os
                          detalhes e corrigir os problemas.
                        </div>
                        {validationData?.errorFileUrl && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() =>
                              window.open(validationData.errorFileUrl, '_blank')
                            }
                          >
                            <i className="ri-download-line me-1"></i>
                            {t('download-errors')}
                          </button>
                        )}
                      </div>
                    )} */}

                    <CsvImporter
                      onClose={() => setActiveTab(2)}
                      onComplete={(data) => {
                        return setPayload({ ...data });
                      }}
                      dataHandler={(raw) => {
                        const rows = raw?.reduce(
                          (acc, i) => acc + i,
                          payload.rows
                        );
                        setPayload({ rows });
                      }}
                      onFileValidation={(file, data) =>
                        handleFileValidation(file, data)
                      }
                      fields={[
                        {
                          name: 'document',
                          label: t('import-document-prop'),
                        },
                        {
                          name: 'name',
                          label: t('import-name-prop'),
                          optional: true,
                        },
                        {
                          name: 'email',
                          label: t('import-email-prop'),
                          optional: true,
                        },
                        {
                          name: 'birthdate',
                          label: t('import-birthdate-prop'),
                          optional: true,
                        },
                        {
                          name: 'cellphone',
                          label: t('import-cellphone-prop'),
                          optional: true,
                        },
                        {
                          name: 'cashbackamount',
                          label: t('import-cashbackamount-prop'),
                          optional: true,
                        },
                        {
                          name: 'cashbackexpiresat',
                          label: t('import-cashbackexpiresat-prop'),
                          optional: true,
                        },
                      ]}
                    />
                  </>
                ),
              },

              {
                label: tReady && t('incentive-import-modal-step2-heading'),
                component: (
                  <>
                    <h6>
                      {tReady && t('incentive-import-modal-step2-heading')}
                    </h6>
                    <p className="d-w-100">
                      {tReady && t('incentive-import-modal-step2-description')}
                    </p>

                    <Row>
                      <Col lg={12}>
                        <div className="mb-3">
                          <Label
                            htmlFor="incentiveGroupId"
                            className="form-label"
                          >
                            {t('incentive-import-cashback-incentive-group')}
                            <span className="text-danger">*</span>
                          </Label>
                          <Select
                            value={groups.find(
                              (option) =>
                                option.value === payload.incentiveGroupId
                            )}
                            onChange={(selectedOption) => {
                              setPayload({
                                incentiveGroupId: selectedOption?.value,
                              });
                            }}
                            options={groups}
                            id="incentiveGroupId"
                            placeholder={t('select')}
                          />
                        </div>
                      </Col>

                      {!hasCashbackAmount && (
                        <Col lg={6}>
                          <div className="mb-3">
                            <Label
                              htmlFor="cashback_amount"
                              className="form-label"
                            >
                              {t('incentive-import-cashback-amount')}
                              <span className="text-danger">*</span>
                            </Label>
                            <CurrencyInput
                              className="form-control"
                              id="cashback_amount"
                              placeholder={t(
                                'incentive-import-cashback-amount'
                              )}
                              value={payload.cashbackAmount}
                              onChange={(value) => {
                                setPayload({
                                  cashbackAmount: value,
                                });
                              }}
                            />
                          </div>
                        </Col>
                      )}

                      {!hasCashbackExpiresAt && (
                        <Col lg={6}>
                          <div className="mb-3">
                            <Label
                              htmlFor="cashback_expires_at"
                              className="form-label"
                            >
                              {t('incentive-import-cashback-expires-at')}
                              <span className="text-danger">*</span>
                            </Label>
                            <Flatpickr
                              className="form-control"
                              id="cashback_expires_at"
                              placeholder={t('select-date')}
                              options={{
                                dateFormat: 'd/m/Y',
                              }}
                              value={payload.cashbackExpiresAt}
                              onChange={(date) => {
                                setPayload({
                                  cashbackExpiresAt: date[0],
                                });
                              }}
                            />
                          </div>
                        </Col>
                      )}
                    </Row>

                    <div className="d-flex justify-content-end mt-3">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => setActiveTab(3)}
                        disabled={!payload.incentiveGroupId}
                      >
                        {t('next') || 'Próximo'}
                      </button>
                    </div>
                  </>
                ),
              },

              {
                label: tReady && t('incentive-import-modal-step3-heading'),
                component: (
                  <>
                    <h6>
                      {tReady && t('incentive-import-modal-step3-heading')}
                    </h6>
                    <p className="d-w-100">
                      {tReady && t('incentive-import-modal-step3-description')}
                    </p>

                    <Table bordered striped>
                      <tbody>
                        <tr>
                          <th style={{ width: '30%' }} scope="row">
                            {t('incentive-import-review-amount')}
                          </th>
                          <td>
                            {payload?.rows} {t('items')}
                          </td>
                        </tr>

                        {payload?.totalIncentiveAmount && (
                          <tr>
                            <th style={{ width: '30%' }} scope="row">
                              {t('incentive-import-review-total-amount')}
                            </th>
                            <td>
                              {formatCurrency(
                                payload?.totalIncentiveAmount
                              )}
                            </td>
                          </tr>
                        )}

                        {payload?.cashbackAmount && (
                          <tr>
                            <th style={{ width: '30%' }} scope="row">
                              {t('incentive-import-review-cashback-amount')}
                            </th>
                            <td>{payload?.cashbackAmount}</td>
                          </tr>
                        )}
                        {payload?.cashbackExpiresAt && (
                          <tr>
                            <th style={{ width: '30%' }} scope="row">
                              {t('incentive-import-review-cashback-expires-at')}
                            </th>
                            <td>
                              {payload?.cashbackExpiresAt &&
                                formatDate(
                                  payload?.cashbackExpiresAt,
                                  'DD/MM/YYYY 23:59'
                                )}
                            </td>
                          </tr>
                        )}

                        <tr>
                          <th style={{ width: '30%' }} scope="row">
                            {t(
                              'incentive-import-review-cashback-incentive-group'
                            )}
                          </th>
                          <td>
                            {payload?.incentiveGroupId && (
                              <>
                                {
                                  groups.find(
                                    (group) =>
                                      group.value === payload.incentiveGroupId
                                  )?.label
                                }
                              </>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    <div className="hstack gap-2 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={resetState}
                        disabled={isLoading}
                      >
                        {t('close')}
                      </button>

                      <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isLoading}
                      >
                        {t('confirm')}
                      </button>
                    </div>
                  </>
                ),
              },
            ]}
          />
        </ModalBody>
      </Form>
    </Modal>
  );
};

export default withTranslation()(IncentiveImporter);
